import { Button } from "@healthos/ui";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Optional override for the fallback UI. */
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

const CHUNK_LOAD_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed/;

export function isChunkLoadError(error: Error | null): boolean {
  return typeof error?.message === "string" && CHUNK_LOAD_ERROR_RE.test(error.message);
}

/**
 * RootErrorBoundary - catches render-time errors and shows a calm recovery
 * screen instead of a white page.
 *
 * Intentionally a thin class component (React's only way to implement
 * `componentDidCatch` / `getDerivedStateFromError` without a third-party
 * dep). The error object is scrubbed before anything leaves this file:
 * only the error name, a trimmed message, and the component stack are
 * logged - never the raw error object or `toString()` output, because
 * those can contain user input from a downstream component's state.
 *
 * See `docs/HealthOS_Technical_Implementation_Guide.md` §9 (observability)
 * for the long-form pattern.
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const componentStack =
      typeof info.componentStack === "string"
        ? info.componentStack.split("\n").slice(0, 6).join("\n")
        : "";
    // Trim message aggressively; skip any content that looks user-derived.
    const message = typeof error.message === "string" ? error.message.slice(0, 240) : "[unknown]";
    // eslint-disable-next-line no-console
    console.error(
      JSON.stringify({
        event: "ui_render_error",
        name: error.name,
        message,
        componentStack,
      }),
    );
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    if (isChunkLoadError(this.state.error)) {
      // A stale tab requested a chunk the latest deploy no longer serves.
      return (
        <div className="flex min-h-screen items-center justify-center bg-cream px-6">
          <div className="max-w-md rounded-xl border border-gray-100 bg-white p-8 shadow-card text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">
              HealthOS
            </p>
            <h1 className="mt-3 font-title text-3xl text-navy">A new version is available</h1>
            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
              HealthOS was updated while this tab was open. Refresh to pick up the latest version.
              Your data is safe.
            </p>
            <Button type="button" className="mt-6" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-6">
        <div className="max-w-md rounded-xl border border-gray-100 bg-white p-8 shadow-card text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">HealthOS</p>
          <h1 className="mt-3 font-title text-3xl text-navy">Something broke on our end</h1>
          <p className="mt-3 text-sm text-gray-700 leading-relaxed">
            A rendering error kept this page from loading. Your data is fine. Try again, or come
            back in a few minutes.
          </p>
          <Button
            type="button"
            className="mt-6"
            onClick={() => {
              this.reset();
              if (typeof window !== "undefined") window.location.assign("/");
            }}
          >
            Back to Today
          </Button>
        </div>
      </div>
    );
  }
}
